import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Check, ShoppingBag, Shield, Zap, ChevronLeft, ChevronRight, Search, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { accountsAPI, categoriesAPI } from '@/utils/api';
import { toast } from 'sonner';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const HomePage = () => {
  const [featuredAccounts, setFeaturedAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        accountsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      const accounts = accountsRes.data;
      setFeaturedAccounts(accounts.filter(a => a.featured).slice(0, 6));
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await accountsAPI.search(searchQuery);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Carousel settings
  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  // Custom arrow components
  function NextArrow(props) {
    const { onClick } = props;
    return (
      <button
        onClick={onClick}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-primary/80 hover:bg-primary flex items-center justify-center transition-all"
        style={{ right: '-20px' }}
      >
        <ChevronRight className="w-6 h-6 text-white" />
      </button>
    );
  }

  function PrevArrow(props) {
    const { onClick } = props;
    return (
      <button
        onClick={onClick}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-primary/80 hover:bg-primary flex items-center justify-center transition-all"
        style={{ left: '-20px' }}
      >
        <ChevronLeft className="w-6 h-6 text-white" />
      </button>
    );
  }

  // Account Card Component
  const AccountCard = ({ account }) => {
    const isOutOfStock = account.stock === 0;
    
    return (
      <Link
        to={`/account/${account.account_id}`}
        className="group relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent hover:border-primary/50 transition-all duration-300 product-card-hover mx-2"
        data-testid={`account-card-${account.account_id}`}
      >
        <div className="relative aspect-video overflow-hidden bg-secondary">
          <img
            src={account.image_url}
            alt={account.title}
            className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${isOutOfStock ? 'opacity-50' : ''}`}
          />
          {/* Coming Soon Badge Overlay */}
          {isOutOfStock && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full border border-yellow-500/30">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm font-bold text-yellow-500">COMING SOON</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="font-heading font-bold text-lg" data-testid={`account-title-${account.account_id}`}>{account.title}</h3>
            {account.stock > 0 ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold" data-testid={`stock-badge-${account.account_id}`}>
                <Check className="w-3 h-3" />
                In Stock
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-500 text-xs font-bold">
                <Clock className="w-3 h-3" />
                Soon
              </span>
            )}
          </div>
          
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{account.description}</p>
          
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs px-2 py-1 rounded-md bg-white/5 border border-white/10">
              Valid for {account.validity_days} days
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <span className="text-2xl font-heading font-black" data-testid={`account-price-${account.account_id}`}>₹{account.price}</span>
            <Button size="sm" className="btn-primary-glow rounded-full" data-testid={`view-button-${account.account_id}`}>
              View Details
            </Button>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-gradient"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1761044590816-5180b35e99eb?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6 backdrop-blur-sm">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Premium Accounts</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-black tracking-tight mb-6" data-testid="hero-title">
              Your Gateway to
              <span className="block text-primary mt-2">Premium Streaming</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-2xl">
              Get instant access to premium streaming accounts. Verified, secure, and delivered to your email within minutes.
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="mb-8 max-w-xl" data-testid="search-form">
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Search for Netflix, Spotify, Disney+..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-white/10 border-white/20 pl-12 pr-24 py-6 text-base rounded-full backdrop-blur-sm"
                  data-testid="search-input"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Button
                  type="submit"
                  disabled={searching}
                  className="absolute right-2 top-1/2 -translate-y-1/2 btn-primary-glow rounded-full"
                  data-testid="search-button"
                >
                  {searching ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </form>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/category/streaming" data-testid="browse-accounts-button">
                <Button size="lg" className="btn-primary-glow rounded-full px-8 py-6 text-base font-bold gap-2">
                  Browse Accounts
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <section className="py-16 bg-white/5" data-testid="search-results-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-heading font-black">
                Search Results ({searchResults.length})
              </h2>
              <Button variant="outline" onClick={clearSearch} className="rounded-full">
                Clear Search
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {searchResults.map((account) => (
                <AccountCard key={account.account_id} account={account} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 border-y border-white/5" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">100% Verified</h3>
              <p className="text-muted-foreground text-sm">All accounts are tested and verified before delivery</p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-accent" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">Instant Delivery</h3>
              <p className="text-muted-foreground text-sm">Get your credentials via email within minutes</p>
            </div>
            
            <div className="text-center p-8">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-2">Secure Payment</h3>
              <p className="text-muted-foreground text-sm">Safe checkout with Razorpay integration</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Accounts */}
      {!loading && featuredAccounts.length > 0 && (
        <section className="py-24" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight mb-4" data-testid="featured-title">
                Featured Accounts
              </h2>
              <p className="text-muted-foreground text-lg">Handpicked premium accounts ready for instant purchase</p>
            </div>
            
            <div className="relative px-8">
              <Slider {...carouselSettings}>
                {featuredAccounts.map((account) => (
                  <AccountCard key={account.account_id} account={account} />
                ))}
              </Slider>
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      {!loading && categories.length > 0 && (
        <section className="py-24 bg-[#121214]" data-testid="categories-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-heading font-black tracking-tight mb-4">
                Browse by Category
              </h2>
              <p className="text-muted-foreground text-lg">Explore our collection of premium accounts</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.category_id}
                  to={`/category/${category.slug}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-primary/50 transition-all duration-300 h-48"
                  data-testid={`category-card-${category.slug}`}
                >
                  <div className="absolute inset-0">
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20"></div>
                  </div>
                  
                  <div className="relative h-full flex flex-col justify-end p-8">
                    <h3 className="text-2xl font-heading font-black mb-2">{category.name}</h3>
                    <p className="text-muted-foreground text-sm">{category.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default HomePage;
